import React from 'react'
import { CreditApplicationStatus } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'

import AccountCreatedBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/AccountCreatedBanner'
import { GetCreditApplicationDetails_creditApplication_CreditApplication } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'

import AwaitingSignatureBanner from './AwaitingSignatureBanner'
import ConfirmIdentityBanner from './ConfirmIdentityBanner'
import CorrectionNeededBanner from './CorrectionNeededBanner'
import FrozenCreditBanner from './FrozenCreditBanner'
import OfferAcceptedBanner from './OfferAcceptedBanner'
import OfferReadyBanner from './OfferReadyBanner'
import RefreshApplicationBanner from './RefreshApplicationBanner'
import ResumeApplicationBanner from './ResumeApplicationBanner'
import ReviewMigrationBanner from './ReviewMigrationBanner'
import styles from './styles.module.scss'
import UploadDocumentsBanner from './UploadDocumentsBanner'

type ActionItem =
  | 'provide_documents'
  | 'provide_corrections'
  | 'provide_identity'
  | 'review_offer'
  | 'unfreeze_credit'
  | 'refresh_application'
  | 'resume_application'
  | 'review_migration'
  | 'create_password'

export interface ActionsNeededSectionProps {
  creditApplication: GetCreditApplicationDetails_creditApplication_CreditApplication
  setActiveTabToDocuments: () => void
}

const ActionsNeededSection = ({ creditApplication, setActiveTabToDocuments }: ActionsNeededSectionProps) => {
  const [getClassName] = useNameFormatter('ActionsNeededSection')

  const { salesforceCreditId, applicationActionItems, personaInquiries, dsaVersion, authorizedSigner, status } =
    creditApplication || {}
  const authSignerEmail = authorizedSigner?.email || ''

  const hasActionItem = (requested: ActionItem) => applicationActionItems?.find(({ action }) => action === requested)

  return (
    <div className={styles[getClassName('banners-container')]}>
      {/* Action items */}
      {/* From onboarding */}
      {hasActionItem('refresh_application') && <RefreshApplicationBanner salesforceCreditId={salesforceCreditId} />}
      {hasActionItem('resume_application') && <ResumeApplicationBanner salesforceCreditId={salesforceCreditId} />}
      {hasActionItem('review_migration') && (
        <ReviewMigrationBanner
          dsaVersion={dsaVersion}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {hasActionItem('create_password') && (
        <AccountCreatedBanner
          authorizedSigner={authorizedSigner}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {/* from compliance */}
      {hasActionItem('provide_corrections') && <CorrectionNeededBanner salesforceCreditId={salesforceCreditId} />}
      {hasActionItem('provide_documents') && (
        <UploadDocumentsBanner setActiveTabToDocuments={setActiveTabToDocuments} />
      )}
      {hasActionItem('provide_identity') && (
        <ConfirmIdentityBanner
          personaInquiries={personaInquiries}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {/* from underwriting */}
      {hasActionItem('review_offer') && (
        <OfferReadyBanner
          authorizedSignerEmail={authSignerEmail}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {hasActionItem('unfreeze_credit') && (
        <FrozenCreditBanner
          authSignerEmail={authSignerEmail}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {/* status derived banners */}
      {status === CreditApplicationStatus.AWAITING_SIGNATURE && (
        <AwaitingSignatureBanner salesforceCreditId={salesforceCreditId} />
      )}
      {status === CreditApplicationStatus.OFFER_ACCEPTED && <OfferAcceptedBanner />}
    </div>
  )
}

export default ActionsNeededSection
